import React from "react";
import { useGetLegalDates } from "../../api/queries";

interface PrivacyContentProps {
  showLastUpdated?: boolean;
}

const PrivacyContent: React.FC<PrivacyContentProps> = ({
  showLastUpdated = true,
}) => {
  const { data: legalDates } = useGetLegalDates();

  const privacyLastUpdated = legalDates?.privacyLastUpdated
    ? new Date(legalDates.privacyLastUpdated).toLocaleDateString()
    : "Loading...";

  return (
    <>
      {showLastUpdated && (
        <p className="text-sm text-gray-600 mb-4">
          Last updated: {privacyLastUpdated}
        </p>
      )}

      <div className="space-y-6 text-gray-800">
        <section>
          <h2 className="text-2xl font-bold mb-4">
            Summary of Privacy Practices
          </h2>
          <p className="mb-4">
            At Psych for Life<sup>®</sup> (&quot;we&quot; &quot;our&quot; or
            &quot;us&quot;), your privacy and the security of your information
            are paramount.
          </p>
          <p className="mb-4">
            The Psych for Life<sup>®</sup> application includes skill-building
            activities designed to help you turn what you learn into actions to
            address challenges, boost wellbeing, improve relationships, increase
            career success, and strengthen community. As part of these
            activities, you will have the opportunity to enter information about
            your thoughts, feelings, behaviors, and plans. Providing such
            information within the application will enhance the effectiveness
            for you of these activities, although there is no requirement that
            you provide any particular type of information, including personal
            information.
          </p>
          <p className="mb-4 italic">
            The most important points for you to know about your privacy and the
            security of your information:
          </p>
          <ol className="list-decimal pl-6 space-y-2 italic">
            <li>
              Your information is encrypted and stored in a secure database to
              protect it from unauthorized access.
            </li>
            <li>
              The personal information you provide while registering an account
              (username and email address) is held separately from any
              information you enter into the application.
            </li>
            <li>
              Only you and a select group of authorized Psych for Life
              <sup>®</sup> team members may access the information you enter
              within the application, and team members may not access this
              information in a way that can identify it as yours.
            </li>
            <li>
              We will not provide your employer or our third-party partners with
              information you enter within the application linkable to you
              personally.
            </li>
            <li>
              We will not use the data you enter within the application for
              training or operating artificial intelligence systems. If we
              decide to change this practice, we will let you know so you may
              opt out.
            </li>
            <li>
              Should law enforcement or governmental agencies request access to
              your data, Psych for Life<sup>®</sup> will comply with applicable
              laws and regulations, but only after a rigorous review process to
              ensure your rights are respected.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Longer-Form Privacy Notice
          </h2>
          <p className="mb-4">
            This Privacy Notice provides information required by Arizona law
            about Psych for Life<sup>®</sup>&apos;s online information
            gathering and dissemination practices.
          </p>
          <p className="mb-4">
            In addition to the information in this Privacy Notice, Psych for
            Life<sup>®</sup>&apos;s Terms and Conditions set forth requirements
            you must follow when using Psych for Life<sup>®</sup>.
          </p>
          <p className="mb-4">
            Please read this notice carefully to understand our policies and
            practices regarding your information and how we treat it. By
            interacting with our website, applications, products and/or services
            (our &quot;Services&quot;) or providing us with your information,
            you agree to the collection, use, and sharing of your information as
            described in this privacy notice. This notice may change from time
            to time (see Changes to Our Privacy Notice). Your continued use of
            the Services after we make changes as described here is deemed to be
            acceptance of those changes, so please check the notice periodically
            for updates.
          </p>
          <p className="mb-4">
            If you are located in the European Union/European Economic
            Area/United Kingdom and wish to exercise your rights under GDPR,
            please contact{" "}
            <a
              href="mailto:support@psychforlife.org"
              className="text-blue-500 hover:text-blue-600"
            >
              support@psychforlife.org
            </a>
            . Psych for Life<sup>®</sup> also abides by the{" "}
            <a
              href="https://www.asu.edu/about/privacy#nav-european-union-supplement"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600"
            >
              Arizona State University (ASU) EU Supplemental Privacy Statement
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Information Psych for Life<sup>®</sup> Collects from You
          </h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              We do not intentionally collect data from users under 18 years of
              age. If data for a user under 18 years of age has been
              inadvertently collected, please notify us at{" "}
              <a
                href="mailto:support@psychforlife.org"
                className="text-blue-500 hover:text-blue-600"
              >
                support@psychforlife.org
              </a>{" "}
              and we will delete that information.
            </li>
            <li>
              During the account registration process, we collect information
              such as usernames (which you can choose), email addresses, and the
              code used to gain access to the application.
            </li>
            <li>
              The personal information you provide while registering an account
              is held separately from any information you enter into the
              application.
            </li>
            <li>
              After you have established your identity with Psych for Life
              <sup>®</sup> and have registered for an account with the
              application, you may enter whatever personal information you deem
              useful to engage with the activities of the application. This
              information is encrypted. Only a limited and select group of
              authorized Psych for Life<sup>®</sup> team members can access
              this information.{" "}
              <strong>
                These team members may not, however, access this information in
                a way that can link it to your identity.
              </strong>
            </li>
            <li>
              We also collect other information about what you do within the
              application, such as the particular screens you visit and how
              often and how long you use the application. Only a limited and
              select group of authorized Psych for Life<sup>®</sup> team
              members can access this information.{" "}
              <strong>
                These team members may not, however, access this information in
                a way that can link it to your identity.
              </strong>
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Methods of Collection</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              Some personal information may be received from employers who
              provide information about their employees to enable us to create
              accounts for the employees to use the application.
            </li>
            <li>
              Some information is collected directly from you when you sign up
              for an account, or when engaging with the application, including
              in-application surveys.
            </li>
            <li>
              We do not employ any cookies or web beacons to automatically
              collect data from you.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Purpose of Collection</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              Information collected from you will be employed to understand how
              you are utilizing the application, improve the application&apos;s
              performance, and enhance your experience.
            </li>
            <li>
              Information collected from employers will be used to enable you to
              register with the application and generate verifiable credentials
              which are sent to you.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            How We Use Your Information
          </h2>
          <p className="mb-4">
            We use the information we collect about you or you provide to us,
            including any personal information, to:
          </p>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              Provide you with the Services and any contents, features,
              information, products, or services we make available through the
              Services.
            </li>
            <li>Fulfill any purpose for which you provide it.</li>
            <li>
              Generate insights and reports, identify trends, and make informed
              decisions to improve our application&apos;s functionality.
            </li>
            <li>Provide you with notices about your account.</li>
            <li>
              Notify you when Services updates are available and about changes
              to any products or services we offer or provide through them.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Information Storage and Security
          </h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              <strong>Secure Storage:</strong> We store your information in
              secure databases. Your data are encrypted in transit and at rest
              to protect it from unauthorized access.
            </li>
            <li>
              <strong>Anonymous Data:</strong> There is no direct connection
              between your personal login or email address and the entries you
              provide as part of actively using the application. We made this
              design choice so that your responses will remain private and
              separate from your personal identity.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Your Rights and Access to Your Data
          </h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              <strong>Your Access:</strong> You can view your own responses and
              related data in various formats throughout the Psych for Life
              <sup>®</sup> application.
            </li>
            <li>
              You may request deletion of your account, including all
              information you have provided us. To do so, email us at{" "}
              <a
                href="mailto:support@psychforlife.org"
                className="text-blue-500 hover:text-blue-600"
              >
                support@psychforlife.org
              </a>
              . Once your account is deleted, you will no longer have access to
              the Psych for Life<sup>®</sup> application or Services.
            </li>
            <li>
              <strong>Our Access:</strong> A very small number of authorized
              Psych for Life<sup>®</sup> team members may access the
              information you enter within the application, but only in a way
              that doesn&apos;t enable them to identify you as the provider of
              that information. Other team members may see general usage data,
              but only in a way that aggregates across users to maintain the
              personal privacy of individual users. We are committed to never
              accessing or displaying data in ways that could identify any
              individual user.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Who We Disclose Your Information To
          </h2>
          <p className="mb-4">
            We do not disclose confidential information we collect online to
            individuals or entities not affiliated with Psych for Life
            <sup>®</sup>, except in the limited circumstances described below.
          </p>
          <p className="mb-4">
            Non-confidential information may be disclosed or distributed
            pursuant to federal and state laws, including Arizona&apos;s public
            records laws, and Arizona State University and Arizona Board of
            Regents (ABOR) policies. These laws and policies explain what
            information may be shared or disclosed. They also explain what
            information is protected as confidential.
          </p>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              We may disclose aggregated information about users, and
              information that does not identify any individual, without
              restriction, including disclosing aggregated information about
              users to employers or other sponsoring organizations.{" "}
              <i>
                We will not provide your employer, sponsoring organization, or
                third-party partners your responses or usage data in a way that
                is identifiable as yours.
              </i>{" "}
              We may provide your employers or sponsoring organizations with
              general usage reports or access to usage dashboards that contain
              data aggregated over many employees or users (e.g., information
              about the most popular topics or overall application usage
              trends), but again:{" "}
              <strong>
                We will not provide your employer, sponsoring organization, or
                third-party partners your responses or usage data in a way that
                is identifiable as yours.
              </strong>
            </li>
            <li>
              We may disclose <strong>only non-confidential information</strong>{" "}
              to certain third parties pursuant to Arizona&apos;s public records
              laws{" "}
              <a
                href="https://www.azleg.gov/arsDetail/?title=39"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-600"
              >
                ARS §§ 39-121 through 39-161
              </a>
              .
            </li>
            <li>
              We may also disclose personal information we collect or you
              provide as described in this Privacy Notice, under very limited
              circumstances as stated below:
              <ol className="list-lower-alpha pl-6 mt-2 space-y-2">
                <li>
                  To contractors or service providers we use to support our
                  organization and{" "}
                  <strong>
                    who are bound by contractual obligations to keep personal
                    information confidential
                  </strong>{" "}
                  and use it only for the purposes for which we disclose it to
                  them.
                </li>
                <li>
                  To a buyer or other successor in the event of a merger,
                  divestiture, restructuring, reorganization, dissolution, or
                  other sale or transfer of some or all of Psych for Life
                  <sup>®</sup>
                  &apos;s assets, whether as a going concern or as part of
                  bankruptcy, liquidation, or similar proceeding, in which
                  personal data held by Psych for Life<sup>®</sup> is among the
                  assets transferred.
                </li>
                <li>
                  To law enforcement entities pursuant to a valid court order or
                  lawfully issued and served subpoena, search warrant, or other
                  legal order. In addition, we may disclose confidential
                  information to law enforcement if we believe that disclosure
                  is necessary to protect us, to protect the health or safety of
                  individuals, or if law enforcement believes that our resources
                  have been used in the commission of a crime.{" "}
                  <strong>
                    Such requests will be subject to a rigorous review process
                    to ensure your rights are respected.
                  </strong>
                </li>
                <li>To fulfill the purpose for which you provide it.</li>
              </ol>
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Cookies and Tracking Technologies; Use of Artificial Intelligence
          </h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              We do not use cookies or other similar types of tracking
              technologies.
            </li>
            <li>
              No AI Training: We will not use your data for training or
              operating artificial intelligence systems. Any future changes to
              this practice will be communicated clearly to you, and with
              sufficient notification time, so you may choose how to participate
              or whether to opt out.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Changes to Our Privacy Notice
          </h2>
          <p className="mb-4">
            This Privacy Notice may be revised from time to time. If we make any
            significant changes, especially changes affecting how personal
            information is collected, stored, or shared, we will notify you the
            next time you log in to Psych for Life<sup>®</sup>. If you opt-in
            to receive e-mails or texts from us, we will also alert you to any
            changes through those means. We encourage you to review this notice
            periodically to stay informed about how we are protecting your
            information. Changes to our privacy notice will not affect the
            privacy of your personal information previously entered into the
            application unless you explicitly allow for that.
          </p>
          <p className="mb-4">
            If you have any questions or concerns regarding your privacy or this
            notice, please contact our support team at{" "}
            <a
              href="mailto:support@psychforlife.org"
              className="text-blue-500 hover:text-blue-600"
            >
              support@psychforlife.org
            </a>
            .
          </p>
        </section>

        <footer className="pt-6 border-t border-gray-200">
          <p className="text-lg font-medium text-center">
            <strong>
              Your trust is important to us, and we remain dedicated to ensuring
              that your personal information remains private and secure. Thank
              you for including Psych for Life<sup>®</sup> in your life
              journey!
            </strong>
          </p>
        </footer>
      </div>
    </>
  );
};

export default PrivacyContent;
